import React from 'react';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { Category } from '@/lib/api';
import { PeriodFilter } from './PeriodFilter';
import type { UseFiltersResult, FilterType } from '@/hooks/useFilters';

export interface FilterBarProps {
  filters: UseFiltersResult;
  /** When true, render Tipo + Categoria controls (Transações). */
  includeTransactionFilters?: boolean;
  categories?: Category[];
  /**
   * When false, the period is a calendar month only: "Período personalizado" and its
   * "Voltar para o mês" return are hidden. Pair it with `useFilters({ monthOnly: true })`.
   */
  allowCustomRange?: boolean;
  className?: string;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  includeTransactionFilters = false,
  categories = [],
  allowCustomRange = true,
  className,
}) => {
  const {
    applied,
    pending,
    isDirty,
    setRange,
    setMode,
    setType,
    setCategory,
    apply,
    applyMonth,
    applyReset,
  } = filters;

  const inRangeMode = pending.mode === 'range' || applied.mode === 'range';
  const hasNonDefaultTxFilters =
    includeTransactionFilters &&
    (applied.type !== 'all' ||
      applied.category !== 'all' ||
      pending.type !== 'all' ||
      pending.category !== 'all');
  const showLimpar = inRangeMode || hasNonDefaultTxFilters;
  const showAplicar = isDirty;
  const showRangeToggle = allowCustomRange && pending.mode === 'month' && !inRangeMode;
  const showMonthToggle = allowCustomRange && pending.mode === 'range';

  /**
   * Centered layout: only on pages with a single control (no Transação filters)
   * and only when the bar is at rest — default month mode, nothing dirty,
   * not in range mode.
   */
  const isQuietCenteredState =
    !includeTransactionFilters && !inRangeMode && !isDirty && !hasNonDefaultTxFilters;

  const handleVoltarParaMes = () => {
    applyMonth(pending.period);
  };

  const periodControl = (
    <PeriodFilter
      mode={pending.mode}
      pendingPeriod={pending.period}
      pendingFrom={pending.from}
      pendingTo={pending.to}
      onMonthPick={applyMonth}
      onRangeChange={setRange}
    />
  );

  const rangeToggleBtn = showRangeToggle && (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={() => setMode('range')}
      className="text-muted-foreground hover:text-foreground"
    >
      Período personalizado
    </Button>
  );

  /** A quieter version of the range toggle used in the quiet centered layout —
   * sits in the corner as a secondary option, not a peer of the stepper. */
  const rangeToggleQuiet = showRangeToggle && (
    <button
      type="button"
      onClick={() => setMode('range')}
      className="text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline"
    >
      Período personalizado
    </button>
  );

  const monthToggleBtn = showMonthToggle && (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={handleVoltarParaMes}
      className="text-muted-foreground hover:text-foreground"
    >
      <ChevronLeft className="h-3.5 w-3.5" />
      Voltar para o mês
    </Button>
  );

  const limparBtn = showLimpar && (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={applyReset}
      className="text-muted-foreground hover:text-foreground"
    >
      Limpar
    </Button>
  );

  const aplicarBtn = showAplicar && (
    <Button
      type="button"
      size="sm"
      variant="default"
      onClick={apply}
      className="h-9 px-4 font-semibold transition-colors"
      aria-label="Aplicar filtros"
    >
      Aplicar Filtros
    </Button>
  );

  if (isQuietCenteredState) {
    return (
      <section
        className={cn('animate-fade-in border-y border-border/60 py-4', className)}
        aria-label="Filtros"
      >
        <div className="flex items-center gap-x-4">
          <div className="flex-1" aria-hidden="true" />
          {periodControl}
          <div className="flex-1 flex justify-end">{rangeToggleQuiet}</div>
        </div>
      </section>
    );
  }

  return (
    <section
      className={cn('animate-fade-in border-y border-border/60 py-4', className)}
      aria-label="Filtros"
    >
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        {periodControl}

        {includeTransactionFilters && (
          <>
            <Select value={pending.type} onValueChange={(v) => setType(v as FilterType)}>
              <SelectTrigger className="h-9 w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                <SelectItem value="income">Receita</SelectItem>
                <SelectItem value="expense">Despesa</SelectItem>
              </SelectContent>
            </Select>

            <Select value={pending.category} onValueChange={setCategory}>
              <SelectTrigger className="h-9 w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as categorias</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}

        <div className="ml-auto flex items-center gap-3">
          {rangeToggleBtn}
          {monthToggleBtn}
          {limparBtn}
          {aplicarBtn}
        </div>
      </div>
    </section>
  );
};

FilterBar.displayName = 'FilterBar';
